import React, { FC, useEffect, useMemo, useState } from 'react';
import { Empty } from '@maxtropy/components';
import { apiV2DistributionCabinetMonitorCode2UrlPost } from '@maxtropy/device-customer-apis-v2';
import {
  HlsVideo,
  HKPlayerFrame,
  RtspPlayerFrame,
  getEzvizTranscodeUrl,
  isEzopenUrl,
  isRtspUrl,
  shouldResolveEzvizUrl,
} from '@/components/RecorderPreview';
import pageStyles from '../index.module.scss';
import { AccessMode, ConfigType } from '@/pages/VideoMonitoring/type';

export interface VideoCardPreviewItem {
  renderKey?: string;
  accessType?: number;
  accountAccessMode?: number;
  configType?: number;
  platformId?: number;
  thirdCode?: string;
  monitorUrl?: string;
  monitorLocation?: string;
  cameraUsername?: string;
  cameraPassword?: string;
  ipAddress?: string;
  port?: number;
  cameraPort?: number;
  rtspPort?: number;
  channelIndex?: number;
  enableFeature?: string;
}

interface VideoCardPreviewProps {
  item: VideoCardPreviewItem;
}

const VideoCardPreview: FC<VideoCardPreviewProps> = ({ item }) => {
  const [monitorUrl, setMonitorUrl] = useState<string>();
  const isPlatformAccess = item.accessType === 1;
  const canPlayHK =
    !!item.ipAddress && !!item.port && !!item.cameraUsername && !!item.cameraPassword && !!item.rtspPort;
  const isPlatformHK =
    isPlatformAccess && (item.accountAccessMode === AccessMode.HK || (!item.accountAccessMode && canPlayHK));
  const isRtspAccessType = isPlatformAccess && item.accountAccessMode === AccessMode.RTSP;
  const isPlatformYsy =
    isPlatformAccess &&
    (item.accountAccessMode === AccessMode.YSY ||
      (item.accountAccessMode == null &&
        (item.configType === ConfigType.PROXY || isEzopenUrl(item.monitorUrl) || !item.monitorUrl)));
  const hkDomId = useMemo(
    () => `hk_video_view_${String(item.renderKey ?? item.platformId ?? item.thirdCode ?? item.monitorUrl ?? '')}`,
    [item.monitorUrl, item.platformId, item.renderKey, item.thirdCode]
  );

  useEffect(() => {
    let ignore = false;

    if (isPlatformHK) {
      setMonitorUrl(undefined);
      return () => {
        ignore = true;
      };
    }

    const needResolveEzvizUrl =
      isPlatformYsy &&
      shouldResolveEzvizUrl({
        accessMode: AccessMode.YSY,
        configType: item.configType,
        url: item.monitorUrl,
        deviceId: item.platformId,
      });

    if (needResolveEzvizUrl) {
      getEzvizTranscodeUrl(item.platformId)
        .then(url => {
          if (!ignore) {
            setMonitorUrl(url);
          }
        })
        .catch(() => {
          if (!ignore) {
            setMonitorUrl(undefined);
          }
        });
      return () => {
        ignore = true;
      };
    }

    if (isPlatformYsy && (item.configType === ConfigType.PROXY || !item.monitorUrl || isEzopenUrl(item.monitorUrl))) {
      setMonitorUrl(undefined);
      return () => {
        ignore = true;
      };
    }

    if (item.monitorUrl) {
      setMonitorUrl(item.monitorUrl);
      return () => {
        ignore = true;
      };
    }
    if (isPlatformYsy && item.platformId != null) {
      getEzvizTranscodeUrl(item.platformId)
        .then(url => {
          if (!ignore) {
            setMonitorUrl(url);
          }
        })
        .catch(() => {
          if (!ignore) {
            setMonitorUrl(undefined);
          }
        });
      return () => {
        ignore = true;
      };
    }
    if (item.accessType === 2 && item.thirdCode) {
      apiV2DistributionCabinetMonitorCode2UrlPost({ code: item.thirdCode })
        .then(res => {
          if (!ignore) {
            setMonitorUrl(res?.url);
          }
        })
        .catch(() => {
          if (!ignore) {
            setMonitorUrl(undefined);
          }
        });
      return () => {
        ignore = true;
      };
    }
    setMonitorUrl(undefined);
    return () => {
      ignore = true;
    };
  }, [
    item.monitorUrl,
    item.accessType,
    item.accountAccessMode,
    item.configType,
    item.platformId,
    item.thirdCode,
    isPlatformAccess,
    isPlatformHK,
    isPlatformYsy,
  ]);

  if (isPlatformHK) {
    if (!canPlayHK) {
      return (
        <div className={pageStyles.preview}>
          <Empty className={pageStyles.previewEmpty} description="暂无视频" />
        </div>
      );
    }
    return (
      <div className={pageStyles.preview}>
        <HKPlayerFrame
          domId={hkDomId}
          monitorLocation={item.monitorLocation}
          cameraUsername={item.cameraUsername}
          cameraPassword={item.cameraPassword}
          ipAddress={item.ipAddress}
          port={item.port}
          cameraPort={item.cameraPort}
          rtspPort={item.rtspPort}
          channelIndex={item.channelIndex}
          enableFeature={item.enableFeature}
        />
      </div>
    );
  }

  const shouldUseRtspIframe = isRtspAccessType || isRtspUrl(monitorUrl);
  if (shouldUseRtspIframe) {
    return (
      <div className={pageStyles.preview}>
        <RtspPlayerFrame rtspUrl={monitorUrl ?? item.monitorUrl} width="100%" height="100%" />
      </div>
    );
  }

  if (!monitorUrl) {
    return (
      <div className={pageStyles.preview}>
        <Empty className={pageStyles.previewEmpty} description="暂无视频" />
      </div>
    );
  }

  return (
    <div className={pageStyles.preview}>
      {item.accessType === 2 ? (
        <HlsVideo src={monitorUrl} code={item.thirdCode} height="100%" minHeight={150} />
      ) : (
        <HlsVideo src={monitorUrl} height="100%" minHeight={150} />
      )}
    </div>
  );
};

export default VideoCardPreview;
